/* eslint-disable react-hooks/exhaustive-deps */
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Checkbox, DatePicker, Form, Input, message, Popconfirm, Select, Table, TableProps } from 'antd';
import InputMask from 'react-input-mask';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'phone' | 'select' | 'date' | 'text' | 'bool';
  validationRules: any;
  record: any;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  validationRules,
  options,
  ...restProps
}) => {
  const customFormat = (value: dayjs.Dayjs) => `${value.format('MM/DD/YYYY')}`;

  const getInputNode = () => {
    switch (inputType) {
      case 'phone':
        return <InputMask mask='(999) 999 9999' maskChar='_' >{(inputProps) => <Input {...inputProps} className='w-full' />}</InputMask>;
      case 'select':
        return <Select showSearch optionFilterProp="label" options={options} />;
      case 'date':
        return <DatePicker format={customFormat} />;
      case 'bool':
        return <Checkbox />;
      default:
        return <Input />;
    }
  };
  
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item className='m-0' name={dataIndex} rules={validationRules ?? []} valuePropName={inputType === 'bool' ? 'checked' : 'value'}>
          {getInputNode()}
        </Form.Item>
      ) : inputType === 'select' && options ? (
        options.find((option: any) => option.value === record[dataIndex])?.label || children
      ) : inputType === 'date' ? (
        record[dataIndex] ? dayjs(record[dataIndex]).format('MM/DD/YYYY') : null
      ) : inputType === 'bool' ? (
        <Checkbox checked={record[dataIndex]} disabled />
      ) : (
        children
      )}
    </td>
  );
};

const CustomHeaderCell = (props: any) => {
  return (
    <th {...props} style={{ padding: '0.5em 1em 0.5em 1em', ...props.style }}>
      {props.children}
    </th>
  );
}

interface EditableTableProps<T> {
  source: T[];
  columns: ColumnsType<T>;
  actions?: any;
  name: string;
  loading: boolean;
  selection: any;
  updateFn: (row: T) => void;
  deleteFn: (id: number) => void;
  scrollYValue?: string;
}

function EditableTable<T extends object>({
  source,
  columns,
  actions,
  name,
  loading,
  selection,
  updateFn,
  deleteFn,
  scrollYValue
}: EditableTableProps<T>) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [editingKey, setEditingKey] = useState(0);

  useEffect(() => {
    if(selection) {
      edit(selection);
    }
  }, [selection]);

  const isEditing = (record: any) => record.id === editingKey;

  const actionColumn = [{
    title: 'Actions',
    fixed: 'right',
    width: actions ? 160 : 60,
    dataIndex: 'operation',
    render: (_: any, record: any) => {
      const editable = isEditing(record);
      return editable ? (
        <span className='flex flex-row mr-0'>
          <CheckOutlined onClick={() => save(record.id!)} className='mr-4' title='Save'/>
          <Popconfirm title='Discard changes?' icon={null} okText='Yes' cancelText='No' onConfirm={() => cancel(record)}>
            <CloseOutlined title='Cancel' />
          </Popconfirm>
        </span>
      ) : (
        <span className='flex flex-row'>
          {actions && actions(record)}
          <EditOutlined onClick={() => edit(record)} className='mr-4' title='Edit'/>
          <Popconfirm title={`Delete this ${name}?`} icon={null} okText='Yes' cancelText='No' onConfirm={() => remove(record.id!)}>
            <DeleteOutlined title='Delete' className='mr-4'/>
          </Popconfirm>
        </span>
      );
    },
  }];

  const remove = (id: number) => {
    deleteFn(id);
    messageApi.open({ type: 'success', content: `${name} deleted correctly` });
  }

  const edit = (record: any) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id!);
  };

  const cancel = (record: any) => {
    const isEmptyRow = Object.entries(record).filter(([key]) => key !== 'id').every(([, value]) => value === undefined);
    if(isEmptyRow) deleteFn(record.id);
    setEditingKey(0);
  };

  const save = async (id: number) => {
    try {
      const row = (await form.validateFields()) as any;
      console.log(row)
      row.id = id;
      updateFn(row);
      setEditingKey(0);
      messageApi.open({ type: 'success', content: `${name} information updated correctly` });
    } catch {
      messageApi.open({ type: 'error', content: 'Please, correct the errors and try again' });
    }
  };

  const mergedColumns: TableProps<T>['columns'] = [...columns, ...actionColumn].map((col: any) => {
    if (!col.editable) return col;
    
    return {
      ...col,
      onCell: (record: T) => ({
        record,
        inputType: col.inputType ?? 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        validationRules: col.validationRules ?? null,
        editing: isEditing(record),
        options: col.options,
      }),
    };
  });

  return (
    <>
      {contextHolder}
      <Form form={form} component={false}>
        <Table<T>
          components={{ body: { cell: EditableCell }, header: { cell: CustomHeaderCell } }}
          bordered
          loading={loading}
          dataSource={source}
          columns={mergedColumns}
          rowClassName='editable-row'
          scroll={{ x: 'max-content', y: scrollYValue }}
          pagination={{ 
            pageSizeOptions: ['25', '50', '100'], 
            defaultPageSize: 50,
          }}
        />
      </Form>
    </>
  )
};

export default EditableTable;
